/**
 * Client-side X25519 keypair + WK_user-wrapped private key. Extracted from
 * wisermail-web/src/services/e2eKeypair.ts (wisermail#83).
 *
 * Wrapped private key:  nonce(12) ‖ AES-256-GCM(WK_user, priv) ‖ tag(16) = 60 bytes.
 * tweetnacl provides the X25519 primitives; pinned to 1.0.3 (byte-locked to the
 * Rust crypto_box server side).
 */

import nacl from 'tweetnacl';

export const X25519_PUBLIC_KEY_LEN = 32;
export const X25519_PRIVATE_KEY_LEN = 32;
export const WRAPPED_PRIVATE_KEY_LEN = 60; // nonce(12) || ct(32) || tag(16)
const WK_NONCE_LEN = 12;

/** A generated X25519 keypair, plaintext bytes only — do NOT persist. */
export interface RawKeypair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

/** Generate a fresh X25519 keypair (32 random bytes → matching public key). */
export function generateKeypair(): RawKeypair {
  const privateKey = new Uint8Array(X25519_PRIVATE_KEY_LEN);
  crypto.getRandomValues(privateKey);
  const pair = nacl.box.keyPair.fromSecretKey(privateKey);
  return { publicKey: pair.publicKey, privateKey: pair.secretKey };
}

/** Derive the public key from a known private key. */
export function publicKeyFromPrivate(privateKey: Uint8Array): Uint8Array {
  if (privateKey.length !== X25519_PRIVATE_KEY_LEN) {
    throw new Error(`privateKey must be ${X25519_PRIVATE_KEY_LEN} bytes; got ${privateKey.length}`);
  }
  return nacl.box.keyPair.fromSecretKey(privateKey).publicKey;
}

/** Wrap a 32-byte private key with WK_user → 60-byte blob. */
export async function wrapPrivateKey(privateKey: Uint8Array, wkUser: Uint8Array): Promise<Uint8Array> {
  if (privateKey.length !== X25519_PRIVATE_KEY_LEN) {
    throw new Error(`privateKey must be ${X25519_PRIVATE_KEY_LEN} bytes; got ${privateKey.length}`);
  }
  if (wkUser.length !== 32) throw new Error(`wkUser must be 32 bytes; got ${wkUser.length}`);
  const key = await crypto.subtle.importKey('raw', wkUser as BufferSource, { name: 'AES-GCM' }, false, ['encrypt']);
  const nonce = new Uint8Array(WK_NONCE_LEN);
  crypto.getRandomValues(nonce);
  const ctWithTag = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, privateKey as BufferSource),
  );
  const out = new Uint8Array(WRAPPED_PRIVATE_KEY_LEN);
  out.set(nonce, 0);
  out.set(ctWithTag, WK_NONCE_LEN);
  if (out.length !== WRAPPED_PRIVATE_KEY_LEN) {
    throw new Error(`wrap produced ${out.length} bytes; expected ${WRAPPED_PRIVATE_KEY_LEN}`);
  }
  return out;
}

/** Unwrap a 60-byte wrapped private key with WK_user. Throws on wrong WK / corruption. */
export async function unwrapPrivateKey(wrapped: Uint8Array, wkUser: Uint8Array): Promise<Uint8Array> {
  if (wrapped.length !== WRAPPED_PRIVATE_KEY_LEN) {
    throw new Error(`wrapped must be ${WRAPPED_PRIVATE_KEY_LEN} bytes; got ${wrapped.length}`);
  }
  if (wkUser.length !== 32) throw new Error(`wkUser must be 32 bytes; got ${wkUser.length}`);
  const key = await crypto.subtle.importKey('raw', wkUser as BufferSource, { name: 'AES-GCM' }, false, ['decrypt']);
  const nonce = wrapped.slice(0, WK_NONCE_LEN);
  const ctWithTag = wrapped.slice(WK_NONCE_LEN);
  const plaintext = new Uint8Array(
    await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, ctWithTag as BufferSource),
  );
  if (plaintext.length !== X25519_PRIVATE_KEY_LEN) {
    throw new Error(`unwrap produced ${plaintext.length} bytes; expected ${X25519_PRIVATE_KEY_LEN}`);
  }
  return plaintext;
}
